import { toast } from "sonner";
import type { ToastOptions } from "./types";

/**
 * Module-scope dedup map: dedupeKey → timestamp of last emit.
 * Using a module-scope Map (not a React ref) ensures it persists across renders
 * and component remounts.
 */
const _recentToasts = new Map<string, number>();

const DEDUPE_WINDOW_MS = 3000;

function shouldSkip(key: string | undefined): boolean {
  if (!key) return false;
  const now = Date.now();
  // Purge stale entries
  for (const [k, ts] of _recentToasts.entries()) {
    if (now - ts > DEDUPE_WINDOW_MS) _recentToasts.delete(k);
  }
  const last = _recentToasts.get(key);
  return last !== undefined && now - last <= DEDUPE_WINDOW_MS;
}

function markEmitted(key: string | undefined): void {
  if (key) _recentToasts.set(key, Date.now());
}

function emitError(title: string, opts?: ToastOptions): void {
  if (shouldSkip(opts?.dedupeKey)) return;
  markEmitted(opts?.dedupeKey);
  toast.error(title, { description: opts?.description });
}

function emitWarning(title: string, opts?: ToastOptions): void {
  if (shouldSkip(opts?.dedupeKey)) return;
  markEmitted(opts?.dedupeKey);
  toast.warning(title, { description: opts?.description });
}

function emitInfo(title: string, opts?: ToastOptions): void {
  if (shouldSkip(opts?.dedupeKey)) return;
  markEmitted(opts?.dedupeKey);
  toast.info(title, { description: opts?.description });
}

export const notify = {
  error: emitError,
  warning: emitWarning,
  info: emitInfo,
};
